import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import { Icon, Label, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { installableAppUploadApk, installableAppUploadLicenseAgreement } from 'client/api/appStoreApi';
import { ApiError } from 'client/api/error';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormErrors } from 'client/hocs/types';
import { Channel } from 'client/model/skill';
import { ApkInfoWithTargets, InstallableAppFormSettings, LicenseAgreement } from 'client/types';
import { translateError } from 'client/utils/errorMessage';
import * as apkKeyset from 'i18n/ApkFormFields';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/Settings';

import styles from './FilesUploaders.module.scss';

type DropzoneState = ReturnType<typeof useDropzone>;

const t = i18n(keyset);
const tAppForm = i18n(appFormKeyset);
const tApk = i18n(apkKeyset);

interface ScreenshotUploaderProps {
    channel: Channel;
    isDragActive: boolean;
    isLoading: boolean;
}

export const ScreenshotUploader = ({ channel, isDragActive, isLoading }: ScreenshotUploaderProps) => {
    const displaySmartScreenResolution = channel === Channel.SmartScreenApp || channel === Channel.AutoApp;

    const stylesContainer = clx(
        styles.appIcon__container,
        styles.appIcon__container_screenshot,
        styles.appIcon_empty,
        styles.appIcon_smallBorderStroke,
        displaySmartScreenResolution && styles.appIcon_smartScreen,
    );
    if (isLoading) {
        return (
            <div className={stylesContainer}>
                <Loader size="s" />
            </div>
        );
    }
    if (isDragActive) {
        return (
            <div className={stylesContainer}>
                <div>{tAppForm('load-here')}</div>
            </div>
        );
    }
    return (
        <div className={stylesContainer}>
            <Icon data={FileArrowUp} size={15} />
            <div className={styles.appIcon__resolution}>{displaySmartScreenResolution ? '1920x1200' : '1920x1080'}</div>
            <div className={clx(styles.appIcon__label, styles.appIcon__label_small)}>{tAppForm('screenshot')}</div>
        </div>
    );
};

const createLabel = (
    isLoading: boolean,
    error: string,
    isDragActive: boolean,
    disabled: boolean,
    dataInfo?: string,
) => {
    if (error) return <Label theme="danger">{error}</Label>;
    if (isLoading) return <Label theme="info">{`...${t('loading')}`}</Label>;
    if (dataInfo)
        return (
            <Label theme={disabled ? 'unknown' : 'info'}>
                {dataInfo.length > 30 ? `...${dataInfo.slice(dataInfo.length - 30)}` : dataInfo}
            </Label>
        );
    if (isDragActive) return <Label theme="normal">{tAppForm('drop-files-here')}</Label>;
    return <Label theme={disabled ? 'unknown' : 'normal'}>{tAppForm('drag-files-here')}</Label>;
};

const FileUploaderView: FC<{
    disabled: boolean;
    error: string;
    isDragActive: boolean;
    isLoading: boolean;
    getInputProps: DropzoneState['getInputProps'];
    getRootProps: DropzoneState['getRootProps'];
    value?: string;
}> = ({ disabled, error, getInputProps, getRootProps, isDragActive, isLoading, value }) => (
    <div
        className={clx(
            'g-text-input',
            'g-text-input_view_normal',
            'g-text-input_size_xl',
            'g-text-input_pin_round-round',
            styles.uploaderContainer,
            disabled && 'g-text-input_disabled',
            disabled && styles.uploaderContainer_disabled,
        )}
    >
        <div className="g-text-input__content">
            <div
                className={clx(styles.uploaderContent, 'g-text-input__control', 'g-text-input__control_type_input')}
                {...getRootProps()}
            >
                <input {...getInputProps()} />
                {createLabel(isLoading, error, isDragActive, disabled, value)}
                <div className={styles.uploaderIcon}>
                    <FileArrowUp />
                </div>
            </div>
        </div>
    </div>
);

export const FileUploaderInput: FC<{
    disabled: boolean;
    uploadedData?: LicenseAgreement;
    onUploadComplete: (data?: unknown) => void;
    fieldName: string;
    error?: string;
    setErrors: (errors: FormErrors<InstallableAppFormSettings & object>, cb?: () => void) => void;
}> = ({ disabled, onUploadComplete, uploadedData, fieldName, error, setErrors }) => {
    const { aliceApp } = useContext(CurrentAliceAppContext)!;
    const [isLoading, setIsLoading] = useState(false);
    const onDrop = useCallback((acceptedFiles) => {
        setIsLoading(true);
        installableAppUploadLicenseAgreement(aliceApp.id, aliceApp.channel, acceptedFiles[0])
            .then((uploadedAgreement) => {
                onUploadComplete(uploadedAgreement);
                setIsLoading(false);
            })
            .catch((err) => {
                let { message } = err;

                if (err instanceof ApiError) {
                    if (err.code === 413) {
                        message = t('upload-error-max-size');
                    } else if (err.code === 415) {
                        message = t('upload-error-format');
                    }
                }
                onUploadComplete(undefined);
                setErrors({ [fieldName]: translateError(message) });
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled });

    return (
        <FileUploaderView
            disabled={disabled}
            error={error || ''}
            getInputProps={getInputProps}
            getRootProps={getRootProps}
            isDragActive={isDragActive}
            isLoading={isLoading}
            value={uploadedData?.name}
        />
    );
};

const useFileProcessing = (
    file: File | undefined,
    appId: string,
    channel: Channel,
    versionNotes: string | undefined,
    packageName: string | undefined,
    versionName: string | undefined,
    updateAppVersion: (updatedValues: Partial<ApkInfoWithTargets>) => void,
    onApkUpdate: () => void,
    setIsApkLoading: React.Dispatch<React.SetStateAction<boolean>>,
) => {
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>();

    useEffect(() => {
        if (versionName) {
            setUploadedFileName(versionName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const setup = async () => {
            if (!file) return;
            setIsLoading(true);
            setIsApkLoading(true);
            setError(undefined);
            installableAppUploadApk({
                appId,
                channel,
                file,
                versionNotes,
            })
                .then((uploadResult) => {
                    if (packageName && packageName !== uploadResult.packageName) {
                        throw new Error(tApk('apk-packages-names-should-be-the-same'));
                    }
                    setUploadedFileName(uploadResult.versionName || '');
                    updateAppVersion(uploadResult);
                    onApkUpdate();
                })
                .catch((err) => {
                    const { message } = err;
                    updateAppVersion({
                        versionNotes,
                        appVersionId: undefined,
                        url: undefined,
                    });
                    setError(translateError(message));
                })
                .finally(() => {
                    setIsLoading(false);
                    setIsApkLoading(false);
                });
        };
        setup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file, channel, appId, setIsApkLoading]);

    return { uploadedFileName, isLoading, error };
};

export const ApkFileUploaderInput: FC<{
    disabled: boolean;
    updateAppVersion: (updatedValues: Partial<ApkInfoWithTargets>) => void;
    validationError?: string;
    packageName?: string;
    versionName?: string;
    versionNotes?: string;
    onApkUpdate: () => void;
    setIsApkLoading: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
    disabled,
    validationError,
    versionName,
    packageName,
    versionNotes,
    updateAppVersion,
    onApkUpdate,
    setIsApkLoading,
}) => {
    const { aliceApp } = useContext(CurrentAliceAppContext)!;
    const [file, setFile] = useState<File>();

    const { uploadedFileName, isLoading, error } = useFileProcessing(
        file,
        aliceApp.id,
        aliceApp.channel,
        versionNotes,
        packageName,
        versionName,
        updateAppVersion,
        onApkUpdate,
        setIsApkLoading,
    );

    const onDrop = useCallback((acceptedFiles) => {
        setFile(acceptedFiles[0]);
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, disabled, accept: ['.apk'] });

    return (
        <FileUploaderView
            disabled={disabled}
            error={error || (!isLoading && validationError) || ''}
            getInputProps={getInputProps}
            getRootProps={getRootProps}
            isDragActive={isDragActive}
            isLoading={isLoading}
            value={uploadedFileName}
        />
    );
};
